import { Box, Button, Drawer, Grid, Link } from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import {
  BrandTrackerOption,
  InboxOption,
  InboxSidebarOption,
  InboxSidebarOptionKey,
  OutreachContactsContext,
  ScheduledOption,
  ScheduledType,
  SentOption,
} from "contexts/OutreachContacts";
import { TargetedCheckInsProvider } from "contexts/TargetedCheckIns";
import { TrackingColumnsContext } from "contexts/TrackingColumns";
import { UserIntegrationsContext } from "contexts/UserIntegrations";
import { useContext, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { Navigate, Route, Routes } from "react-router-dom";
import { routes } from "schemas/routes";

import CloseDealButton from "features/Home/CloseDealButton";
import UploadGmail from "features/Influencer/Tracking/ImportDialog/UploadGmail";
import { isPage } from "utils/general";

import { useOutreachTable } from "../useOutreachTable";
import BrandTracker from "./BrandTracker";
import Checkins from "./Checkins";
import EmailList from "./EmailList";
import Scheduled from "./Scheduled";
import CancelAllButton from "./Scheduled/ScheduledSidebar/CancelAll";
import InboxSearchBarMenu from "./SearchBar/menu";
import Sidebar from "./Sidebar";
import styles from "./styles";

const OutreachTable = () => {
  const { currentOrg } = useContext(OrganizationUserContext);
  const { getContactProperties } = useContext(TrackingColumnsContext);
  const { userIntegration } = useContext(UserIntegrationsContext);
  const {
    fetchLoading,
    hitsLimit,
    fetchContactsParams,
    currentSidebarOption,
    handleFetchContacts,
    fetchTotals,
  } = useContext(OutreachContactsContext);
  const { isSmallScreen } = useOutreachTable();

  const [ref, isRefVisible] = useInView({
    rootMargin: "0px 0px",
  });

  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const [searchbarOpen, setSearchbarOpen] = useState<boolean>(false);
  const [showShareContactsDialog, setShowShareContactsDialog] =
    useState<boolean>(false);

  const fetchTheRightPage = (option: InboxSidebarOption) => {
    const updatedParams = { ...fetchContactsParams };
    updatedParams.page = 1;
    updatedParams.selectedSuggestion = null;
    updatedParams.importantOnly = false;
    updatedParams.statuses = option.statuses;
    updatedParams.searchQuery = undefined;
    if (option.key === InboxSidebarOptionKey.scheduled) {
      updatedParams.scheduledType = ScheduledType.outreach;
    }
    if (option.key === InboxSidebarOptionKey.brandTracker) {
      updatedParams.perPage = 20;
      updatedParams.ignorePinnedFirst = true;
      updatedParams.includeBentoBrandCategories = true;
      updatedParams.source = "Brand Tracker";
    } else {
      updatedParams.perPage = undefined;
      updatedParams.ignorePinnedFirst = undefined;
      updatedParams.includeBentoBrandCategories = undefined;
      updatedParams.source = undefined;
    }
    handleFetchContacts(updatedParams);
  };

  const switchPage = () => {
    if (isPage(routes.inboxScheduled)) {
      fetchTheRightPage(ScheduledOption);
    } else if (isPage(routes.inboxSent)) {
      fetchTheRightPage(SentOption);
    } else if (isPage(routes.inboxBrandTracker)) {
      fetchTheRightPage(BrandTrackerOption);
    } else {
      fetchTheRightPage(InboxOption);
    }
  };

  useEffect(() => {
    if (isRefVisible) {
      if (!hitsLimit && !fetchLoading) {
        const updatedParams = { ...fetchContactsParams };
        updatedParams.page += 1;
        handleFetchContacts(updatedParams);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefVisible, fetchLoading]);

  useEffect(() => {
    switchPage();
    fetchTotals();
    getContactProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  useEffect(() => {
    if (userIntegration?.id && !userIntegration?.partnershipsSearchedAt) {
      setShowShareContactsDialog(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIntegration?.id]);

  const toggleSidebar = (newOpen: boolean) => () => {
    setSidebarOpen(newOpen);
  };

  return (
    <>
      <Grid container>
        {isSmallScreen ? (
          <Grid
            container
            justifyContent="space-between"
            item
            xs={12}
            sx={{ py: 0.5 }}
          >
            <Button size="small" color="primary" onClick={toggleSidebar(true)}>
              <Box
                component="i"
                className="fa-solid fa-chevron-left"
                sx={{ mr: 1 }}
              />{" "}
              {currentSidebarOption?.label}{" "}
            </Button>

            {isPage(routes.inboxCheckins) ? (
              <Link
                sx={{ px: 1, textDecoration: "none" }}
                href="https://www.loom.com/share/18d18ef271c847458d0a9a999fe2c6f7?sid=d375d598-5215-4457-9dfa-be9ae6036362"
                target="_blank"
              >
                View Tutorial{" "}
                <Box
                  component="i"
                  className="fa-regular fa-arrow-up-right-from-square"
                />
              </Link>
            ) : isPage(routes.inboxBrandTracker) ? (
              <Box />
            ) : (
              <Box>
                {isPage(routes.inboxScheduled) && <CancelAllButton />}{" "}
                <Button
                  color="secondary"
                  onClick={() => setSearchbarOpen((prev) => !prev)}
                  sx={{ textTransform: "none", fontSize: 13 }}
                >
                  Search{" "}
                  <Box
                    sx={{ ml: 1 }}
                    component="i"
                    className="fa-solid fa-search"
                  />
                </Button>
              </Box>
            )}
            {searchbarOpen && !isPage(routes.inboxBrandTracker) && (
              <Grid item xs={12}>
                <InboxSearchBarMenu />
              </Grid>
            )}
          </Grid>
        ) : (
          <Grid item md={2}>
            <CloseDealButton />

            <Box sx={{ mt: 2, width: "100%", pr: 1.5 }}>
              <Sidebar
                setShowShareContactsDialog={setShowShareContactsDialog}
                displaySyncContacts={
                  !userIntegration?.partnershipsSearchedAt ||
                  (!currentOrg?.isVaUser && currentOrg?.allowShareContacts)
                }
              />
            </Box>
          </Grid>
        )}
        <Grid
          item
          container
          alignItems="flex-start"
          xs={12}
          md={isSmallScreen ? 12 : 10}
        >
          <Grid
            item
            xs={12}
            sx={[
              styles.contacts,
              currentSidebarOption?.key === InboxSidebarOptionKey.inbox ||
              currentSidebarOption?.key === InboxSidebarOptionKey.sent
                ? { height: "calc(100vh - 100px)" }
                : {},
            ]}
          >
            <Routes>
              <Route
                path="*"
                element={<Navigate to={`/${routes.inboxMain}`} replace />}
              />
              <Route path="inbox/*" element={<EmailList ref={ref} />} />
              <Route
                path="scheduled/*"
                element={
                  <Scheduled
                    isSmallScreen={isSmallScreen}
                    sidebarOpen={sidebarOpen}
                  />
                }
              />
              <Route path="sent/*" element={<EmailList ref={ref} />} />
              <Route
                path="checkins/*"
                element={
                  <TargetedCheckInsProvider>
                    <Checkins isSmallScreen={isSmallScreen} />
                  </TargetedCheckInsProvider>
                }
              />
              <Route path="brand-tracker/*" element={<BrandTracker />} />
            </Routes>
          </Grid>
        </Grid>
      </Grid>

      <Drawer
        PaperProps={{
          sx: { minWidth: "200px", height: "100vh" },
        }}
        open={sidebarOpen}
        onClose={toggleSidebar(false)}
      >
        <Sidebar
          setShowShareContactsDialog={setShowShareContactsDialog}
          displaySyncContacts={
            !userIntegration?.partnershipsSearchedAt ||
            (!currentOrg?.isVaUser && currentOrg?.allowShareContacts)
          }
          onSelect={toggleSidebar(false)}
          isSmallScreen
        />
      </Drawer>

      <UploadGmail
        open={showShareContactsDialog}
        handleClose={() => setShowShareContactsDialog(false)}
      />
    </>
  );
};
export default OutreachTable;
